import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { membersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { errorHandler } from '../../utils/errorHandler';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ErrorDisplay from '../../components/common/ErrorDisplay/ErrorDisplay';
import './Members.css';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

// Custom arrow components
const PrevArrow = (props) => {
  const { onClick } = props;
  return (
    <div className="slick-arrow custom-arrow custom-prev" onClick={onClick}>
      <FaChevronLeft />
    </div>
  );
};

const NextArrow = (props) => {
  const { onClick } = props;
  return (
    <div className="slick-arrow custom-arrow custom-next" onClick={onClick}>
      <FaChevronRight />
    </div>
  );
};

function Members({ members: propMembers }) {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { currentUser } = useAuth();
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalMembers, setTotalMembers] = useState(0);
    const membersPerPage = 12;

    useEffect(() => {
        if (propMembers) {
            if (propMembers.length > 0 && typeof propMembers[0] === 'string') {
                fetchMemberDetails(propMembers);
            } else {
                setMembers(propMembers);
                setLoading(false);
            }
            return;
        }

        fetchMembers();
    }, [currentUser, propMembers, currentPage]);

    const fetchMembers = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await membersAPI.getAllMembers(currentPage, membersPerPage);
            if (response && response.members) {
                setMembers(response.members);
                setTotalMembers(response.totalCount);
                setTotalPages(response.totalPages);
            } else {
                setMembers([]);
                setTotalMembers(0);
                setTotalPages(1);
            }
        } catch (error) {
            console.error('Error fetching members:', error);
            setError(errorHandler.processError(error).message);
            setMembers([]);
            setTotalMembers(0);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const fetchMemberDetails = async (memberIds) => {
        try {
            setLoading(true);
            const response = await membersAPI.getMembersByIds(memberIds);
            if (response) {
                setMembers(response);
            } else {
                setMembers([]);
            }
        } catch (error) {
            setError(errorHandler.processError(error).message);
            setMembers([]);
        } finally {
            setLoading(false);
        }
    };

    // Show loading spinner while data is being fetched
    if (loading) {
        return <LoadingSpinner />;
    }

    // Show error if there is one
    if (error) {
        return <ErrorDisplay message={error} />;
    }

    // If no members, show appropriate message
    if (!members || members.length === 0) {
        return (
            <div className="members-container">
                <h1>Our Members</h1>
                <p>No members found.</p>
            </div>
        );
    }

    const settings = {
        dots: true,
        infinite: members.length > 3,
        speed: 500,
        slidesToShow: Math.min(3, members.length),
        slidesToScroll: 1,
        autoplay: members.length > 3,
        autoplaySpeed: 1500,
        pauseOnHover: true,
        prevArrow: <PrevArrow />,
        nextArrow: <NextArrow />,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: Math.min(2, members.length),
                    slidesToScroll: 1,
                }
            },
            {
                breakpoint: 600,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1
                }
            }
        ]
    };

    // Define role priority order
    const rolePriority = {
        'President': 1,
        'Vice President': 2,
        'Second Vice President': 3,
        'Third Vice President': 4,
        'General Secretary': 5,
        'Deputy General Secretary': 6,
        'Treasurer': 7,
        'Deputy Treasurer': 8,
        'Communication Coordinator': 9,
        'Advisor in charge of Projects': 10,
        'Advisor in charge of Software Engineering': 11,
        'Advisor in charge of Digital Transformation and Artificial Intelligence': 12
    };

    // Filter board members
    const boardMembers = members.filter(member => member && rolePriority[member.role]);

    // Sort board members by role priority
    const sortedBoardMembers = [...boardMembers].sort((a, b) => {
        const priorityA = rolePriority[a.role] || 999;
        const priorityB = rolePriority[b.role] || 999;
        return priorityA - priorityB;
    });

    // Sort regular members alphabetically
    const sortedMembers = [...members].sort((a, b) => {
        return (a.name || '').localeCompare(b.name || '');
    });

    // If this is being rendered from the home page (propMembers exists), only show the slider
    if (propMembers) {
        return (
            <div className='members-slider-wrapper'>
                <Slider {...settings}>
                    {sortedBoardMembers.map((member) => (
                        <div key={member.id} className='member-slide'>
                            <div className='home-member-card'>
                                <div className='home-member-image-wrap'>
                                    <img 
                                        src={member.picture || '/images/default-profile.png'} 
                                        alt={member.name} 
                                        className='member-image'
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = '/images/default-profile.png';
                                        }}
                                    />
                                </div>
                                <div className='member-info'>
                                    <h2>{member.name}</h2>
                                    <p>{member.role}</p>
                                    <div className='member-social'>
                                        {member.social?.linkedin && (
                                            <a href={member.social.linkedin} target="_blank" rel="noopener noreferrer">
                                                <i className='fab fa-linkedin' />
                                            </a>
                                        )}
                                        {member.social?.facebook && (
                                            <a href={member.social.facebook} target="_blank" rel="noopener noreferrer">
                                                <i className='fab fa-facebook' />
                                            </a>
                                        )}
                                        {member.social?.instagram && (
                                            <a href={member.social.instagram} target="_blank" rel="noopener noreferrer">
                                                <i className='fab fa-instagram' />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </Slider>
            </div>
        );
    }

    // Full members page view
    return (
        <div className="members-container">
            <h1>Our Members</h1>
            <div className="members-grid">
                {sortedMembers.map(member => (
                    <div key={member.id} className="member-card">
                        <div className="member-image-wrap">
                            <img 
                                src={member.picture || member.profileImage || member.imageUrl || '/images/default-profile.png'} 
                                alt={member.name} 
                                className="member-image"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = '/images/default-profile.png';
                                }}
                            />
                        </div>
                        <div className="member-info">
                            <h2>{member.name}</h2>
                            {member.role && <p className="member-role">{member.role}</p>}
                            <div className="member-social">
                                {member.social?.linkedin && (
                                    <a href={member.social.linkedin} target="_blank" rel="noopener noreferrer">
                                        <i className="fab fa-linkedin" />
                                    </a>
                                )}
                                {member.social?.facebook && (
                                    <a href={member.social.facebook} target="_blank" rel="noopener noreferrer">
                                        <i className="fab fa-facebook" />
                                    </a>
                                )}
                                {member.social?.instagram && (
                                    <a href={member.social.instagram} target="_blank" rel="noopener noreferrer">
                                        <i className="fab fa-instagram" />
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {totalPages > 1 && (
                <div className="pagination">
                    <button
                        className="pagination-button"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>
                    <span className="pagination-info">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        className="pagination-button"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}

export default Members;
